import { Eye, Printer } from 'lucide-react';
import { IconButton } from '@platform/design-system';
import { usePageContext } from '../usePageContext.ts';
import type { Order } from './orders.ts';
import type { AvailableOrderAction, OrderActionKey } from './useAvailableOrderActions.ts';
import styles from './Orders.module.css';

export type OrderRowActionsProps = {
  order: Order;
  available: readonly AvailableOrderAction[];
  busy: boolean;
  mayPrintLabel: boolean;
  onOpen: (order: Order) => void;
  onAction: (key: OrderActionKey, order: Order) => void;
  onPrintLabel: (shipmentId: string) => void;
};

const ICON_SIZE = 16;

export const OrderRowActions = ({
  order,
  available,
  busy,
  mayPrintLabel,
  onOpen,
  onAction,
  onPrintLabel,
}: OrderRowActionsProps) => {
  const { t } = usePageContext();
  const nameOf = (action: string) => t('common.rowAction', { action, name: order.number });
  const { shipmentId } = order.dispatch;
  const iconFor = ({ key, labelKey, Icon }: AvailableOrderAction) => (
    <IconButton
      key={key}
      size="sm"
      label={nameOf(t(labelKey))}
      icon={<Icon size={ICON_SIZE} aria-hidden="true" />}
      disabled={busy}
      onClick={() => {
        onAction(key, order);
      }}
    />
  );
  return (
    <span className={styles['rowActions']}>
      <IconButton
        size="sm"
        label={nameOf(t('orders.view'))}
        icon={<Eye size={ICON_SIZE} aria-hidden="true" />}
        onClick={() => {
          onOpen(order);
        }}
      />
      {available.filter((action) => action.key !== 'cancel').map(iconFor)}
      {shipmentId === null || !mayPrintLabel ? null : (
        <IconButton
          size="sm"
          label={nameOf(t('orders.labelPrint'))}
          icon={<Printer size={ICON_SIZE} aria-hidden="true" />}
          onClick={() => {
            onPrintLabel(shipmentId);
          }}
        />
      )}
      {available.filter((action) => action.key === 'cancel').map(iconFor)}
    </span>
  );
};
